/**
 * The placeholders a message template may use.
 *
 * One list, imported by both sides, because the two drifted apart the moment
 * there were two. The Templates screen used to advertise {dateStr}, {duration},
 * {zoomJoinUrl} and {rescheduleUrl}, none of which the renderer has ever
 * filled: they belong to the confirmed meeting flow that is deferred to v2
 * (CLAUDE.md section 6). At the same time it failed to mention {showPhrase},
 * {dayOfWeek} and {calendarLink}, which the renderer does fill. Typing one of
 * the first group produced a message with a literal "{zoomJoinUrl}" in it, in
 * front of a prospect.
 *
 * Anything added here must also be added to the values map in
 * worker/lib/messages.ts, and the check in scripts/check-placeholders.mjs
 * fails if the two disagree.
 *
 * `channels` says where a placeholder resolves. {signature} is email only:
 * a signature is an HTML table and there is no sensible text form of it.
 */

export const PLACEHOLDERS = [
  { name: 'firstName', channels: ['sms', 'email'], describes: "The contact's first name, tidied up" },
  { name: 'repName', channels: ['sms', 'email'], describes: 'The rep, full name' },
  { name: 'repFirstName', channels: ['sms', 'email'], describes: 'The rep, first name only' },
  { name: 'repPhone', channels: ['sms', 'email'], describes: "The rep's cell number" },
  { name: 'repEmail', channels: ['sms', 'email'], describes: "The rep's email address" },
  { name: 'showName', channels: ['sms', 'email'], describes: 'The show, e.g. HITEC 2026' },
  { name: 'showPhrase', channels: ['sms', 'email'], describes: 'The show with its city, e.g. HITEC 2026 in San Antonio' },
  { name: 'dayOfWeek', channels: ['sms', 'email'], describes: 'The met on day, e.g. Thursday' },
  { name: 'meetingTopics', channels: ['sms', 'email'], describes: 'What you noted to discuss' },
  { name: 'calendarLink', channels: ['sms', 'email'], describes: "The rep's 30 minute booking link (booking_link_30)" },
  { name: 'vcardUrl', channels: ['sms', 'email'], describes: "The rep's contact card link" },
  { name: 'contact_note', channels: ['email'], describes: 'The note typed on this contact, if any' },
  { name: 'signature', channels: ['email'], describes: 'The full HTML email signature' },
  {
    name: 'personPhoto',
    channels: ['email'],
    describes:
      'The contact\'s person photo, as a src= value. Filled for Initial Email and every reminder stage ' +
      '(same values map), but only placed in Initial Email (no caption) and Last Email (with the ' +
      'parting gift caption) by design, not First or Second Follow Up. If it needs a caption, keep both ' +
      'on the same line, e.g. <p>caption</p><img src="{personPhoto}">, so they disappear together for a ' +
      'contact with no photo.',
  },
]

/** Just the names, for the channel given. */
export function placeholderNames(channel) {
  return PLACEHOLDERS.filter((p) => !channel || p.channels.includes(channel)).map((p) => p.name)
}

/**
 * Every {placeholder} or {~placeholder} in a template that this app cannot
 * fill. The `~` form (see render() in worker/lib/messages.ts) ties a line's
 * fate to a placeholder without printing it, but it is still a reference to
 * a real name, and a typo in it fails exactly as silently as a typo in the
 * printed form would otherwise: the line just always drops.
 *
 * Used to warn while typing rather than after sending. Returns the names
 * without their braces or tilde, in the order they appear, without
 * duplicates.
 */
export function unknownPlaceholders(template, channel) {
  if (!template) return []
  const known = new Set(placeholderNames(channel))
  const found = template.match(/\{~?(\w+)\}/g) || []
  const bad = []
  for (const token of found) {
    const name = token.replace(/^\{~?/, '').slice(0, -1)
    if (!known.has(name) && !bad.includes(name)) bad.push(name)
  }
  return bad
}
